// ─────────────────────────────────────────────────────────────
// ZIFEK — Gestion des types de compte (table typecompte)
// Passe par l'edge function `manage-account-types` (superadmin).
// ─────────────────────────────────────────────────────────────
import { supabase } from '@/lib/supabase';

export interface AccountType {
  id: number;
  nom: string;
  statustypecompte: number;
  page: string;
  image: string;
  ordre: number;
  actif: boolean;
  /** Capacité de boutique (produits / services / mixte / marketplace / …). */
  capacites: string | null;
  /** Type de compte parent (null = type principal). Permet les sous-types. */
  parent_id?: number | null;
}

export interface AccountTypeInput {
  nom: string;
  statustypecompte: number;
  page: string;
  image: string;
  ordre: number;
  actif: boolean;
  capacites: string;
  parent_id?: number | null;
}

interface CallerIdentity {
  userId: number;
  email: string;
}

async function call<T>(identity: CallerIdentity, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('manage-account-types', {
    body: { ...body, user_id: identity.userId, email: identity.email },
  });
  if (error) throw new Error(error.message || 'Erreur de communication avec le back-end.');
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String((data as { error: unknown }).error));
  }
  return data as T;
}

export async function listAccountTypes(identity: CallerIdentity): Promise<AccountType[]> {
  const res = await call<{ types: AccountType[] }>(identity, { action: 'list' });
  return (res.types || []).slice().sort((a, b) => a.id - b.id);
}

export async function createAccountType(identity: CallerIdentity, input: AccountTypeInput): Promise<AccountType> {
  const res = await call<{ type: AccountType }>(identity, { action: 'create', ...input });
  return res.type;
}

export async function updateAccountType(identity: CallerIdentity, id: number, input: AccountTypeInput): Promise<AccountType> {
  const res = await call<{ type: AccountType }>(identity, { action: 'update', id, ...input });
  return res.type;
}

export async function toggleAccountType(identity: CallerIdentity, id: number, actif: boolean): Promise<AccountType> {
  const res = await call<{ type: AccountType }>(identity, { action: 'toggle', id, actif });
  return res.type;
}